import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Stack, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from '@mui/material';
import FunctionsIcon from '@mui/icons-material/Functions';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

interface Props {
  open: boolean;
  /** All data columns, in display order. */
  columns: string[];
  /** Lower-cased names of calculated columns. */
  calcColumns: Set<string>;
  /** Maps a field to its current display heading (override or resolved calc header). */
  headers: Record<string, string>;
  /** Maps a field to its fixed width; absent = auto (flex to fill). */
  widths: Record<string, number>;
  /** Default heading derivation (snake_case -> Title Case). */
  humanize: (col: string) => string;
  onClose: () => void;
  /** Set/clear a SOURCE column heading; empty string clears the override. */
  onChangeHeader: (field: string, header: string) => void;
  /** Set a fixed width, or undefined to revert the column to auto sizing. */
  onChangeWidth: (field: string, width: number | undefined) => void;
  /** Open the calculation editor for a calc column (where its heading lives). */
  onEditCalc?: (field: string) => void;
}

export default function ColumnSettingsDialog({
  open, columns, calcColumns, headers, widths, humanize,
  onClose, onChangeHeader, onChangeWidth, onEditCalc,
}: Props) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        Columns
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
          Rename headings and set widths. Changes are saved with the process.
          Calculated-column headings are edited on their expression.
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Column</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Heading</TableCell>
              <TableCell sx={{ fontWeight: 700, width: 160 }}>Width (px)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {columns.map((col) => {
              const isCalc = calcColumns.has(col.toLowerCase());
              const resolved = headers[col] ?? humanize(col);
              const width = widths[col];
              return (
                <TableRow key={col} hover>
                  <TableCell sx={{ verticalAlign: 'top', py: 1 }}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
                      >
                        {col}
                      </Typography>
                      {isCalc && (
                        <Tooltip title="Calculated column">
                          <FunctionsIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>

                  <TableCell sx={{ py: 1 }}>
                    {isCalc ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip size="small" variant="outlined" label={resolved} sx={{ maxWidth: 220 }} />
                        {onEditCalc && (
                          <Button
                            size="small"
                            startIcon={<OpenInNewIcon fontSize="small" />}
                            onClick={() => { onEditCalc(col); onClose(); }}
                          >
                            Edit expression
                          </Button>
                        )}
                      </Stack>
                    ) : (
                      <TextField
                        size="small"
                        fullWidth
                        value={headers[col] ?? ''}
                        placeholder={humanize(col)}
                        onChange={(e) => onChangeHeader(col, e.target.value)}
                      />
                    )}
                  </TableCell>

                  <TableCell sx={{ py: 1 }}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <TextField
                        size="small"
                        type="number"
                        value={width ?? ''}
                        placeholder="auto"
                        onChange={(e) => {
                          const v = e.target.value.trim();
                          if (v === '') { onChangeWidth(col, undefined); return; }
                          const n = Number(v);
                          if (Number.isFinite(n) && n > 0) onChangeWidth(col, Math.round(n));
                        }}
                        sx={{ width: 100 }}
                        inputProps={{ min: 40, step: 10 }}
                      />
                      <Tooltip title="Reset to auto width">
                        <span>
                          <IconButton
                            size="small"
                            disabled={width === undefined}
                            onClick={() => onChangeWidth(col, undefined)}
                          >
                            <RestartAltIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {columns.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="body2">Run a preview to see columns here.</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} variant="contained">Done</Button>
      </DialogActions>
    </Dialog>
  );
}
